import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import VerifiedIcon from "@mui/icons-material/Verified";

import { PHONE_TYPE } from "../../constants";
import { isVerifiableContact } from "./entityLogic";
import { PHONE_TYPE_LABELS } from "./entityLabels";
import PhoneNumberField from "../generic/PhoneNumberField";
import {
  DEFAULT_PHONE_COUNTRY,
  composeE164,
  countryCodeForName,
  flagFor,
  splitE164,
} from "../../phoneCountries";

// Las listas de correos y teléfonos de una entidad, agrupadas POR PERSONA.
//
// Un contacto ya no es una cadena suelta: cada fila dice de quién es. Si lleva
// `userId` es de alguien con cuenta en la entidad —y entonces su correo es el
// mismo con el que entra a la aplicación—; si no, es un contacto externo y
// tiene que traer un nombre. Sin eso la ficha acumulaba direcciones que nadie
// podía atribuir a nadie.
//
// Las reglas que se pintan aquí las impone handleEntity de verdad; esto sólo
// evita que se pida algo que se va a rechazar:
//   - una persona del equipo tiene UN correo, el de acceso, y no se borra;
//   - una persona del equipo necesita al menos un celular verificado, y encima
//     puede tener los fijos que quiera;
//   - un contacto externo puede ser sólo fijo, y nunca se verifica.
//
// Se comparte entre Mi Entidad (la empresa se administra sola) y la ficha del
// gestor. La diferencia es quién puede verificar: sólo el dueño de la cuenta ve
// el código, así que un contacto que agrega un gestor entra sin verificar.
// `canVerify` es lo que enciende ese botón.
//
// Props:
//   emails / phones   las listas guardadas
//   members           las cuentas de la entidad (LIST_MEMBERS), para agrupar
//   readOnly          sólo lectura (la ficha del gestor sin permiso de edición)
//   canVerify         muestra «Verificar»
//   busy              deshabilita todo mientras hay una acción en vuelo
//   onAdd(channel, contact)
//   onUpdate(channel, id, patch)
//   onSetPrimary(channel, id)
//   onDelete(channel, id)
//   onVerify(channel, contact)   sólo si canVerify

const EMAIL = "EMAIL";
const PHONE = "PHONE";

const EXTERNAL = "__EXTERNAL__";

const memberLabel = (m) =>
  `${(m && m.name) || ""} ${(m && m.lastName) || ""}`.trim() || (m && m.email) || "Sin nombre";

function ContactRow({
  contact,
  channel,
  readOnly,
  canVerify,
  busy,
  onSetPrimary,
  onDelete,
  onVerify,
  onUpdate,
}) {
  const isEmail = channel === EMAIL;
  const verifiable = isEmail || isVerifiableContact(contact);
  const value = isEmail ? contact.address : contact.number;
  // El correo de una cuenta es con el que esa persona entra. Borrarlo dejaría
  // viva una cuenta cuya dirección de acceso la ficha ya no conoce, así que el
  // backend lo rechaza y aquí ni se ofrece: quien ya no pertenece al equipo se
  // retira desde Personal, que es la acción que de verdad significa eso.
  const isLoginEmail = isEmail && !!contact.userId;

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <Box sx={{ pt: 0.5, color: "text.secondary" }}>
          {isEmail ? <EmailOutlinedIcon fontSize="small" />
            : contact.type === PHONE_TYPE.LANDLINE ? <PhoneOutlinedIcon fontSize="small" />
            : <PhoneAndroidIcon fontSize="small" />}
        </Box>

        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
            {/* La bandera va sólo en los teléfonos, y se cae al país que dice el
                indicativo cuando el contacto es anterior al selector de país. */}
            {!isEmail && (
              <Typography component="span" sx={{ mr: 0.5 }}>
                {flagFor(contact.countryCode || splitE164(contact.number).countryCode)}
              </Typography>
            )}
            {value}
            {contact.extension ? ` ext. ${contact.extension}` : ""}
          </Typography>

          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
            {isLoginEmail && (
              <Chip size="small" color="primary" variant="outlined"
                icon={<BadgeOutlinedIcon />} label="Correo de acceso" />
            )}
            {contact.primary && (
              <Chip size="small" color="primary" icon={<StarIcon />} label="Principal" />
            )}
            {!isEmail && (
              <Chip size="small" variant="outlined" label={PHONE_TYPE_LABELS[contact.type] || contact.type} />
            )}
            {contact.verified ? (
              <Chip size="small" color="success" icon={<VerifiedIcon />} label="Verificado" />
            ) : verifiable ? (
              <Chip size="small" color="warning" variant="outlined" label="Sin verificar" />
            ) : (
              // Un fijo no es un contacto «pendiente de verificar»: es que no se
              // puede verificar nunca. Decirlo evita que alguien lo persiga.
              <Tooltip title="A un teléfono fijo no le podemos enviar un código por SMS.">
                <Chip size="small" variant="outlined" label="No verificable" />
              </Tooltip>
            )}
            {/* En el bloque de una persona el nombre ya está en el encabezado;
                en el de externos la etiqueta ES el nombre y sí hay que verla. */}
            {!contact.userId && contact.label && (
              <Chip size="small" variant="outlined" label={contact.label} />
            )}
          </Stack>

          {isEmail && !readOnly && (
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Switch
                  size="small"
                  checked={contact.notify !== false}
                  disabled={busy || !contact.verified}
                  onChange={(e) => onUpdate(channel, contact.id, { notify: e.target.checked })}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Recibe avisos de vencimiento
                </Typography>
              }
            />
          )}
        </Box>

        {!readOnly && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            {canVerify && verifiable && !contact.verified && (
              <Button size="small" variant="outlined" disabled={busy}
                onClick={() => onVerify(channel, contact)} sx={{ textTransform: "none" }}>
                Verificar
              </Button>
            )}
            <Tooltip title={
              contact.primary ? "Ya es el principal"
                : !contact.verified ? "Sólo un contacto verificado puede ser el principal"
                : "Marcar como principal"
            }>
              <span>
                <IconButton size="small" disabled={busy || contact.primary || !contact.verified}
                  onClick={() => onSetPrimary(channel, contact.id)}
                  aria-label={`Marcar ${value} como principal`}>
                  {contact.primary ? <StarIcon fontSize="small" color="primary" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={
              isLoginEmail ? "Es el correo con el que esa persona entra: no se puede eliminar"
                : contact.primary ? "No se puede eliminar el principal"
                : "Eliminar"
            }>
              <span>
                <IconButton size="small" color="error"
                  disabled={busy || contact.primary || isLoginEmail}
                  onClick={() => onDelete(channel, contact)}
                  aria-label={`Eliminar ${value}`}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

// Agregar un contacto. Lo primero que pregunta es DE QUIÉN es, porque de eso
// depende todo lo demás.
//
// Para un correo no hay pregunta: siempre es externo. El correo de alguien del
// equipo es su correo de acceso, ya está en la ficha desde que se creó su
// cuenta, y se cambia con «Cambiar correo» —que además de la ficha tiene que
// tocar Firebase Auth—. Ofrecer aquí «agregarle un correo a María» sólo llevaría
// a un rechazo del servidor.
function AddContactDialog({ channel, open, busy, onClose, onSave, defaultCountry, members }) {
  const isEmail = channel === EMAIL;
  const [owner, setOwner] = useState(EXTERNAL);
  const [type, setType] = useState(PHONE_TYPE.MOBILE);
  const [value, setValue] = useState("");
  const [extension, setExtension] = useState("");
  const [label, setLabel] = useState("");
  // El país arranca en el de la entidad cuando la ficha lo tiene escrito.
  const [countryCode, setCountryCode] = useState(defaultCountry);

  const isExternal = isEmail || owner === EXTERNAL;

  const reset = () => {
    setOwner(EXTERNAL); setType(PHONE_TYPE.MOBILE); setValue(""); setExtension(""); setLabel("");
    setCountryCode(defaultCountry);
  };
  const close = () => { reset(); onClose(); };

  const save = () => {
    const userId = isExternal ? null : owner;
    if (isEmail) { onSave({ address: value, label: label.trim(), userId: null }); reset(); return; }
    // Un celular se guarda en E.164, compuesto con el indicativo del país
    // escogido. Un FIJO se guarda tal como se tecleó —es la regla de siempre,
    // porque "604 444 5566" es válido y E.164 sólo lo estropearía—, pero ahora
    // se lleva su país al lado, que es lo único que le faltaba para significar
    // algo fuera de Colombia.
    onSave({
      number: type === PHONE_TYPE.LANDLINE ? value : composeE164(countryCode, value),
      type,
      countryCode,
      extension: type === PHONE_TYPE.LANDLINE ? extension : "",
      label: isExternal ? label.trim() : "",
      userId,
    });
    reset();
  };

  // Un externo sin nombre es exactamente lo que estas listas dejaron de admitir.
  const missingName = isExternal && !label.trim();

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{isEmail ? "Agregar un correo" : "Agregar un teléfono"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {isEmail ? (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Este correo queda como <strong>contacto externo</strong>. El correo de alguien
              de tu equipo es con el que entra a la aplicación: se cambia desde
              <strong> Personal → Cambiar correo</strong>.
            </Alert>
          ) : (
            <TextField
              select fullWidth size="small" label="¿De quién es este teléfono?"
              value={owner} onChange={(e) => setOwner(e.target.value)}
            >
              {members.map((m) => (
                <MenuItem key={m.userId} value={m.userId}>
                  {memberLabel(m)}{m.position ? ` · ${m.position}` : ""}
                </MenuItem>
              ))}
              <MenuItem value={EXTERNAL}>Contacto externo (no es de mi equipo)</MenuItem>
            </TextField>
          )}

          {!isEmail && (
            <>
              {/* El tipo se escoge después del dueño porque el dueño decide qué
                  tipos tienen sentido: a un celular se le puede mandar el
                  código, a un fijo no. */}
              <ToggleButtonGroup
                exclusive fullWidth size="small" value={type}
                onChange={(_, v) => v && setType(v)}
              >
                <ToggleButton value={PHONE_TYPE.MOBILE}>
                  <PhoneAndroidIcon fontSize="small" sx={{ mr: 1 }} /> Celular
                </ToggleButton>
                <ToggleButton value={PHONE_TYPE.LANDLINE}>
                  <PhoneOutlinedIcon fontSize="small" sx={{ mr: 1 }} /> Teléfono fijo
                </ToggleButton>
              </ToggleButtonGroup>
              <Alert severity="info" sx={{ py: 0 }}>
                {type === PHONE_TYPE.MOBILE
                  ? "Te enviaremos un código por SMS para verificarlo."
                  : isExternal
                    ? "Un teléfono fijo no se puede verificar por SMS: queda registrado como dato de contacto."
                    : "Un fijo se puede agregar encima del celular verificado de esa persona, nunca en su lugar."}
              </Alert>
            </>
          )}

          {isEmail ? (
            <TextField
              autoFocus fullWidth
              label="Correo electrónico"
              type="email"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="facturacion@entidad.com"
            />
          ) : (
            <PhoneNumberField
              autoFocus
              label={type === PHONE_TYPE.LANDLINE ? "Teléfono fijo" : "Celular"}
              placeholder={type === PHONE_TYPE.LANDLINE ? "604 444 5566" : "300 111 2233"}
              countryCode={countryCode}
              national={value}
              onChange={({ countryCode: cc, national }) => { setCountryCode(cc); setValue(national); }}
            />
          )}

          {!isEmail && type === PHONE_TYPE.LANDLINE && (
            <TextField fullWidth label="Extensión (opcional)" value={extension}
              onChange={(e) => setExtension(e.target.value)} />
          )}

          {isExternal && (
            <TextField fullWidth required label="¿A nombre de quién?" value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={isEmail ? "Facturación electrónica" : "Recepción"}
              error={missingName && !!value.trim()}
              helperText="Una persona o un área. Un contacto que nadie responde no le sirve a nadie." />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy || !value.trim() || missingName}>
          Agregar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EntityContacts({
  emails = [],
  phones = [],
  members = [],
  // El país de la entidad, para que un teléfono nuevo no arranque siempre en
  // Colombia cuando la ficha dice otra cosa.
  country = null,
  readOnly = false,
  canVerify = false,
  busy = false,
  error = null,
  onAdd,
  onUpdate,
  onSetPrimary,
  onDelete,
  onVerify,
}) {
  const [adding, setAdding] = useState(null);   // 'EMAIL' | 'PHONE'
  const [confirm, setConfirm] = useState(null); // { channel, contact }

  // Una entrada por persona con cuenta, en el orden en que llegan los miembros,
  // y al final todo lo que no es de nadie del equipo. Un `userId` que ya no
  // corresponde a ninguna cuenta (alguien retirado) cae con los externos en vez
  // de desaparecer de la pantalla.
  const { groups, externalEmails, externalPhones } = useMemo(() => {
    const known = new Set(members.map((m) => m.userId));
    const owned = (list) => list.filter((c) => c.userId && known.has(c.userId));
    const loose = (list) => list.filter((c) => !c.userId || !known.has(c.userId));
    const ownedEmails = owned(emails);
    const ownedPhones = owned(phones);
    return {
      groups: members
        .map((m) => ({
          member: m,
          emails: ownedEmails.filter((c) => c.userId === m.userId),
          phones: ownedPhones.filter((c) => c.userId === m.userId),
        }))
        .filter((g) => g.emails.length || g.phones.length),
      externalEmails: loose(emails),
      externalPhones: loose(phones),
    };
  }, [emails, phones, members]);

  const rows = (channel, list) => list.map((c) => (
    <ContactRow
      key={c.id} contact={c} channel={channel}
      readOnly={readOnly} canVerify={canVerify} busy={busy}
      onSetPrimary={onSetPrimary} onUpdate={onUpdate} onVerify={onVerify}
      onDelete={(ch, contact) => setConfirm({ channel: ch, contact })}
    />
  ));

  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error">{error}</Alert>}

      {/* Los dos botones van arriba de todo y no dentro de una de las dos
          secciones: el de teléfono puede terminar en cualquiera de las dos,
          porque lo primero que pregunta es de quién es el número. */}
      {!readOnly && (
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={busy}
            onClick={() => setAdding(EMAIL)} sx={{ textTransform: "none" }}>
            Agregar correo
          </Button>
          <Button size="small" variant="outlined" startIcon={<AddIcon />} disabled={busy}
            onClick={() => setAdding(PHONE)} sx={{ textTransform: "none" }}>
            Agregar teléfono
          </Button>
        </Stack>
      )}

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Personal de la entidad</Typography>
        {groups.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Todavía nadie del equipo tiene contactos registrados.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {groups.map(({ member, emails: mEmails, phones: mPhones }) => (
              <Box key={member.userId}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <PersonOutlineIcon fontSize="small" color="action" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {memberLabel(member)}
                  </Typography>
                  {member.position && (
                    <Typography variant="caption" color="text.secondary">
                      · {member.position}
                    </Typography>
                  )}
                </Stack>
                <Stack spacing={1} sx={{ pl: { sm: 3 } }}>
                  {rows(EMAIL, mEmails)}
                  {rows(PHONE, mPhones)}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Contactos externos</Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          Correos y teléfonos que no son de una cuenta: una recepción, un buzón de
          facturación. Van a nombre de alguien y no sirven para entrar a la aplicación.
        </Typography>
        {externalEmails.length === 0 && externalPhones.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No hay contactos externos registrados.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {rows(EMAIL, externalEmails)}
            {rows(PHONE, externalPhones)}
          </Stack>
        )}
      </Box>

      <AddContactDialog
        channel={adding} open={!!adding} busy={busy} members={members}
        defaultCountry={countryCodeForName(country) || DEFAULT_PHONE_COUNTRY}
        onClose={() => setAdding(null)}
        onSave={(contact) => { onAdd(adding, contact); setAdding(null); }}
      />

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Eliminar contacto</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2">
              ¿Eliminar «{confirm?.channel === EMAIL ? confirm?.contact?.address : confirm?.contact?.number}»?
            </Typography>
            {confirm?.contact?.verified && confirm?.channel === EMAIL && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Si era uno de los correos con los que se entra a esta ficha, dejará de servir para eso.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancelar</Button>
          <Button color="error" variant="contained" disabled={busy}
            onClick={() => { onDelete(confirm.channel, confirm.contact.id); setConfirm(null); }}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
